import { useSelector } from 'react-redux';
import { Redirect, Route, RouteChildrenProps, Switch } from 'react-router';
import {
  ApprovalWorkflowsGuard,
  ConfigurationChangelogGuard,
  DatasetsGuard,
  LabelsGuard,
} from '../../components/Restrictors';
import { isDevFeatureEnabled } from '../../constants/dev';
import { AppearancePage } from '../../features/appearance/AppearancePage';
import { appearancePath } from '../../features/appearance/constants';
import { ConfigurationChangelogRoutes } from '../../features/configuration-changelog/ConfigurationChangelogRoutes';
import { configurationChangelogPath } from '../../features/configuration-changelog/routes';
import { DatasetsRoutes } from '../../features/datasets';
import { datasetsPath } from '../../features/datasets/routes';
import {
  extensionsPath,
  extensionsStorePath,
} from '../../features/extensions/routes';
import { FieldManagerRoutes } from '../../features/field-manager/field-manager';
import { fieldManagerPath } from '../../features/field-manager/routes';
import { credentialsPath } from '../../features/integration-credentials/core';
import { CredentialsRoutes } from '../../features/integration-credentials/routes';
import { LabelList } from '../../features/labels/components/LabelList';
import { labelsPath } from '../../features/labels/routes';
import { QueueManager, queueManagerPath } from '../../features/queue-manager';
import { ruleManagerPath } from '../../features/rule-manager/routes';
import { RuleManagerRoutes } from '../../features/rule-manager/RuleManagerRoutes';
import { usersPath } from '../../features/users/routes';
import { UsersRoutes } from '../../features/users/UsersRoutes';
import { workflowsSettingsPath } from '../../features/workflows/settings/routes';
import { isEmpoweredManager } from '../../redux/modules/user/selectors';
import { ActivitiesPage } from '../Activities';
import { activitiesPath } from '../Activities/routes';
import { automationPath } from '../Automation/helpers';
import { WorkflowSettingsRoutes } from '../WorkflowRoutes/WorkflowSettingsRoutes';
import { settingsPath } from './helpers';
import SettingsPage from './SettingsPage';

export const SettingsRoutes = (props: RouteChildrenProps) => {
  const { match: settingsRouteMatch } = props;

  const empoweredManager = useSelector(isEmpoweredManager);

  if (!settingsRouteMatch) {
    return null;
  }

  if (empoweredManager) {
    return (
      <Switch>
        <Route path={settingsPath()} exact component={SettingsPage} />
        <Route
          path={labelsPath()}
          render={() => (
            <LabelsGuard>
              <LabelList />
            </LabelsGuard>
          )}
        />
        <Route render={() => <Redirect to={settingsPath()} />} />
      </Switch>
    );
  }

  return (
    <Switch>
      <Route path={settingsPath()} exact component={SettingsPage} />
      <Route path={usersPath()} component={UsersRoutes} />
      <Route path={fieldManagerPath()} component={FieldManagerRoutes} />
      <Route path={ruleManagerPath()} component={RuleManagerRoutes} />
      {isDevFeatureEnabled('appearance') ? (
        <Route path={appearancePath()} component={AppearancePage} />
      ) : null}
      <Route
        path={labelsPath()}
        render={() => (
          <LabelsGuard>
            <LabelList />
          </LabelsGuard>
        )}
      />
      <Route
        path={datasetsPath()}
        render={() => (
          <DatasetsGuard>
            <DatasetsRoutes />
          </DatasetsGuard>
        )}
      />
      <Route path={credentialsPath()} component={CredentialsRoutes} />
      <Route path={activitiesPath()} render={() => <ActivitiesPage />} />
      <Route
        path={configurationChangelogPath()}
        render={() => (
          <ConfigurationChangelogGuard>
            <ConfigurationChangelogRoutes />
          </ConfigurationChangelogGuard>
        )}
      />
      <Route path={queueManagerPath()} render={() => <QueueManager />} />
      <Route
        path={workflowsSettingsPath()}
        render={() => (
          <ApprovalWorkflowsGuard>
            <WorkflowSettingsRoutes />
          </ApprovalWorkflowsGuard>
        )}
      />
      {/* Remove next Switch with redirects when its year 2026+ */}
      <Switch>
        <Route
          path={`${settingsRouteMatch.path}/automation`}
          render={({ location }) => (
            <Redirect
              to={location.pathname.replace(
                `${settingsRouteMatch.path}/automation`,
                automationPath()
              )}
            />
          )}
        />
        <Route
          path={`${settingsRouteMatch.path}${extensionsPath()}`}
          render={({ location }) => (
            <Redirect
              to={location.pathname.replace(
                `${settingsRouteMatch.path}/extensions`,
                extensionsPath()
              )}
            />
          )}
        />
        <Route
          path={`${settingsRouteMatch.path}/store`}
          render={() => <Redirect to={extensionsStorePath()} />}
        />
      </Switch>
      <Route render={() => <Redirect to={settingsPath()} />} />
    </Switch>
  );
};
